import { useEffect, useState } from 'react';
import { UserDocument } from '@/entities/user-document';
import { getDocumentLog } from '@/use-cases/get-document-log';
import { DocumentAuditLog } from '@/entities/document-audit-log';
import { DocumentSigner } from '@/entities/document-signer';
import { getDocumentSigners } from '@/use-cases/get-document-signers';
import { cancelSigningRequest } from '@/use-cases/cancel-signing-request';
import { DataTable } from '@/components/data-table/data-table';
import { ColumnDef } from '@tanstack/react-table';
import styles from './document-audit-log.module.scss';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogFooter,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogAction,
	AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button';
import { toast } from '@/components/toast/toast';
import notifyHB from '@/config/notifyHB';
import { DateTime } from 'luxon';
import { AddSignerDialog } from '../add-signer-dialog/add-signer-dialog';
import {
	History,
	Users,
	UserPlus,
	Loader2,
	AlertCircle,
	TriangleAlert,
	Inbox,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const SIGNER_STATUS_LABELS: Record<DocumentSigner['Status'], string> = {
	PENDING: 'Pendiente',
	SIGNED: 'Firmado',
	CANCELLED: 'Cancelado',
};

const SignerStatusBadge = ({ status }: { status: DocumentSigner['Status'] }) => (
	<span className={cn(styles['signer-status'], styles[status])}>
		{SIGNER_STATUS_LABELS[status] ?? status}
	</span>
);

type DocumentAuditLogProps = {
	document: UserDocument | null;
	onClose: () => void;
	onSignersAdded?: () => void;
};
const documentLogsTableData: ColumnDef<DocumentAuditLog>[] = [
	{
		accessorKey: 'CreatedAt',
		header: 'Fecha',
		cell: ({ row }) => (
			<div className={styles['user-agent']}>
				{DateTime.fromISO(row.original.CreatedAt).toLocaleString(
					DateTime.DATETIME_MED,
				)}
			</div>
		),
	},
	{
		accessorKey: 'UserFullName',
		header: 'Usuario',
		cell: ({ row }) => (
			<div className={styles['user-name']}>
				{row.original.UserFullName?.replace(',', '').toLowerCase()}
			</div>
		),
	},
	{
		accessorKey: 'Action',
	},
	{
		accessorKey: 'UserAgent',
		header: 'Agente',
		cell: ({ row }) => (
			<div className={styles['user-agent']}>{row.original.UserAgent}</div>
		),
		maxSize: 250,
	},
	{
		accessorKey: 'UserIp',
		header: 'Dirección IP',
		maxSize: 250,
	},
];

export const DocumentAuditLogs = (props: DocumentAuditLogProps) => {
	const [isFetchingLogs, setIsFetchingLogs] = useState(false);
	const [documentLogs, setDocumentLogs] = useState<DocumentAuditLog[] | null>(
		null,
	);
	const [isFetchingSigners, setIsFetchingSigners] = useState(false);
	const [documentSigners, setDocumentSigners] = useState<
		DocumentSigner[] | null
	>(null);
	const [signerToCancel, setSignerToCancel] = useState<DocumentSigner | null>(
		null,
	);
	const [isCancellingSigner, setIsCancellingSigner] = useState(false);
	const [isAddSignerDialogOpen, setIsAddSignerDialogOpen] = useState(false);

	const fetchSigners = () => {
		if (!props.document) return;
		setIsFetchingSigners(true);
		getDocumentSigners(props.document.Serial)
			.then(setDocumentSigners)
			.catch((error) => {
				console.debug(error);
				setDocumentSigners([]);
			})
			.finally(() => setIsFetchingSigners(false));
	};

	useEffect(() => {
		if (props.document) {
			setIsFetchingLogs(true);
			getDocumentLog(props.document.Serial)
				.then(setDocumentLogs)
				.catch((error) => {
					console.debug(error);
				})
				.finally(() => setIsFetchingLogs(false));

			fetchSigners();
		} else {
			setDocumentLogs(null);
			setDocumentSigners(null);
		}
	}, [props.document]);

	const confirmCancelSigner = async () => {
		if (!props.document || !signerToCancel) return;
		setIsCancellingSigner(true);
		try {
			const response = await cancelSigningRequest(
				props.document.Serial,
				signerToCancel.Email,
			);
			if (response?.code === 'SUCCESS') {
				toast({
					mode: 'default',
					description: `Se eliminó la solicitud de firma enviada a ${signerToCancel.Email}`,
				});
				fetchSigners();
			} else {
				toast({
					mode: 'destructive',
					description:
						response?.message ??
						'No se pudo eliminar la solicitud de firma',
				});
			}
		} catch (error) {
			notifyHB(
				'document-audit-logs',
				'DocumentAuditLogs',
				`Failed to cancel signing request with ${error}`,
			);
			toast({
				mode: 'destructive',
				description: 'No se pudo eliminar la solicitud de firma',
			});
		} finally {
			setIsCancellingSigner(false);
			setSignerToCancel(null);
		}
	};

	const documentSignersTableData: ColumnDef<DocumentSigner>[] = [
		{
			accessorKey: 'Email',
			header: 'Destinatario',
		},
		{
			accessorKey: 'Status',
			header: 'Estado',
			cell: ({ row }) => <SignerStatusBadge status={row.original.Status} />,
		},
		{
			accessorKey: 'RequestedAt',
			header: 'Fecha de envío',
			cell: ({ row }) =>
				row.original.RequestedAt
					? DateTime.fromISO(row.original.RequestedAt).toLocaleString(
							DateTime.DATETIME_MED,
						)
					: '-',
		},
		{
			id: 'actions',
			header: '',
			cell: ({ row }) =>
				row.original.Status === 'PENDING' ? (
					<Button
						variant="ghost"
						size="sm"
						className="text-destructive hover:text-destructive"
						onClick={() => setSignerToCancel(row.original)}
					>
						Eliminar
					</Button>
				) : null,
		},
	];

	return (
		<>
			<Dialog
				onOpenChange={(open) => (open ? {} : props.onClose())}
				open={props.document !== null && !isAddSignerDialogOpen}
			>
				{props.document && (
					<DialogContent className={styles['document-audit-log']}>
						<div className={styles['header']}>
							<span className={styles['header-icon']}>
								<History />
							</span>
							<span className={styles['title']}>
								{props.document.Path}
							</span>
						</div>
						<div className={styles['body']}>
							<div className={styles['section']}>
								<div className={styles['section-header']}>
									<div
										className={
											styles['section-title-group']
										}
									>
										<Users size={16} strokeWidth={2} />
										<span
											className={styles['section-title']}
										>
											Destinatarios
										</span>
										{documentSigners && (
											<span
												className={
													styles['section-count']
												}
											>
												{documentSigners.length}
											</span>
										)}
									</div>
									{props.document.State === 'Signed' && (
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setIsAddSignerDialogOpen(true)
											}
										>
											<UserPlus />
											Agregar firmante
										</Button>
									)}
								</div>
								{documentSigners ? (
									documentSigners.length > 0 ? (
										<DataTable
											columns={documentSignersTableData}
											data={documentSigners}
											isSortable
										/>
									) : (
										<div className={styles['empty-state']}>
											<Inbox />
											Este documento no tiene
											solicitudes de firma enviadas.
										</div>
									)
								) : isFetchingSigners ? (
									<div className={styles['empty-state']}>
										<Loader2 className="animate-spin" />
										Cargando destinatarios...
									</div>
								) : (
									<div className={styles['error-state']}>
										<AlertCircle />
										No se pudieron cargar los
										destinatarios.
									</div>
								)}
							</div>

							<div className={styles['section']}>
								<div className={styles['section-header']}>
									<div
										className={
											styles['section-title-group']
										}
									>
										<History size={16} strokeWidth={2} />
										<span
											className={styles['section-title']}
										>
											Historial
										</span>
										{documentLogs && (
											<span
												className={
													styles['section-count']
												}
											>
												{documentLogs.length}
											</span>
										)}
									</div>
								</div>
								{documentLogs ? (
									<DataTable
										columns={documentLogsTableData}
										data={documentLogs}
										isSortable
									/>
								) : isFetchingLogs ? (
									<div className={styles['empty-state']}>
										<Loader2 className="animate-spin" />
										Cargando historial...
									</div>
								) : (
									<div className={styles['error-state']}>
										<AlertCircle />
										No se pudo cargar el historial.
									</div>
								)}
							</div>
						</div>
					</DialogContent>
				)}
			</Dialog>
			<AlertDialog
				open={signerToCancel !== null}
				onOpenChange={(open) => (open ? {} : setSignerToCancel(null))}
			>
				<AlertDialogContent
					style={{
						width: 480,
						maxWidth: '92vw',
						padding: 24,
					}}
				>
					<AlertDialogHeader>
						<div className="flex items-center gap-3">
							<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
								<TriangleAlert size={20} />
							</span>
							<AlertDialogTitle
								className={styles['cancel-signer-title']}
							>
								Eliminar solicitud de firma
							</AlertDialogTitle>
						</div>
						<AlertDialogDescription
							className={styles['cancel-signer-description']}
						>
							¿Estás seguro de que deseas eliminar la
							solicitud de firma enviada a{' '}
							<span className={styles['signer-email']}>
								{signerToCancel?.Email}
							</span>
							?
							<span className={styles['warning-note']}>
								Esta acción no se puede deshacer y esta
								persona ya no podrá firmar este documento.
							</span>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isCancellingSigner}>
							Cancelar
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={isCancellingSigner}
							onClick={confirmCancelSigner}
							className={buttonVariants({
								variant: 'destructive',
							})}
						>
							{isCancellingSigner ? (
								<>
									<Loader2 className="animate-spin" />
									Eliminando...
								</>
							) : (
								'Eliminar'
							)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
			<AddSignerDialog
				document={isAddSignerDialogOpen ? props.document : null}
				onClose={() => {
					// Closing this dialog and re-opening the audit-log Dialog
					// in the same tick races their Radix portals, so this
					// closes the whole flow instead of returning to it.
					setIsAddSignerDialogOpen(false);
					props.onClose();
				}}
				onSignersAdded={() => {
					fetchSigners();
					props.onSignersAdded?.();
				}}
			/>
		</>
	);
};
